import { css } from 'twin.macro';
import React, { useState, useRef, useMemo, useEffect, Fragment } from 'react';
import { connect, useSelector } from 'react-redux';
import StaticPreview from '../../Admin/Form/StaticPreview';
import { useForm } from 'react-hook-form';
import { fetchWrapper, getSequenceNumber, getUserRole, isJson } from '../../../_helpers';
import { updateTaskFormStatus } from '../../../_redux/actions/ActionTasks';
import { toast } from 'react-toastify';
import { Button, Form } from 'react-bootstrap';
import { VscLoading } from 'react-icons/vsc';
import styled from '@emotion/styled';
import UserInRoom from '../../Admin/Tasks/UserInRoom';
import Swal from 'sweetalert2';
import { isAllowedDraftFormType } from '@/components/Admin/Form/ViewForm';
import FormPagination from '@/components/Admin/Form/FormPages/FormPagination';
import FormStepper from '@/components/Admin/Form/FormPages/FormStepper';
import { getFormContainerStyles, staticPreviewContainerStyles } from '@/components/Admin/Form/FormPages/styles';
import { handleFormSubmission } from '@/components/Admin/Form/FormPages/utils';
import { addSeenFields, clearSeenFields } from '@/_redux/actions/ActionFormSubmission';
import { useClientMedicationInformation } from '@/components/Admin/Form/hooks/useClientMedicationInformation';
import UserFormDataDiscussion from './UserFormDataDiscussion';

const ParticipantsContainer = styled.div`
    max-height: 300px;
    overflow-y: auto;
`;

const FormSubmissionModal = ({
    user,
    form,
    roomId,
    currRoom,
    team,
    userId,
    handleClose = () => {},
    position,
    formType,
    getAllTasks,
    isSubmitting,
    setIsSubmitting = () => {},
    taskId,
    subTasks = [],
    updateTaskFormStatus,
    visitTemplateId,
    isDisabled = false,
    isPreview = false,
    submissionType,
    userFormData = {},
    sharedFormId,
    sharedUserFormDataId,
    setSharedFormResponse = () => {},
    setSharedUserFormDataResponse = () => {},
    addSeenFields,
    clearSeenFields,
    hideUserFormDataDiscussion = false,
}) => {
    const [userData, setUserData] = useState([]);
    const [isDraft, setIsDraft] = useState(false);
    const [isDraftFormSubmitting, setIsDraftFormSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [validationError, setValidationError] = useState(null);
    const channelMembers = useSelector((state) => state.rooms.channelMembers);
    const formRef = useRef();
    const { register, handleSubmit, errors, trigger, getValues, setError, clearErrors, setValue, watch } = useForm({
        mode: 'onBlur',
    });

    const [selectedUserOption, setSelectedUserOption] = useState([]);
    const { medicationInfo, isLoading } = useClientMedicationInformation(form, watch, setValue);
    const enhanceElement = (element) => {
        try {
            if (!form || (form?.specialityForm || {})?.type !== 'clientMedications') {
                return element;
            }
            const specialityKey = element?.specialityKey || (element?.config || {})?.specialityKey;

            if (specialityKey === 'medicationInfo') {
                const baseInstruction =
                    element.instruction ||
                    'Medication information including doctor-prescribed dosage and frequency, and FDA-derived details will appear here based on the selected medication.';

                const parts = [baseInstruction];

                if (medicationInfo) {
                    parts.push(medicationInfo);
                } else if (isLoading) {
                    parts.push('Fetching medication information...');
                }

                return {
                    ...element,
                    instruction: parts.join('\n\n'),
                };
            }

            return element;
        } catch (error) {
            console.log('#enhanceElement', error);
            return element;
        }
    };
    const hasPageBreak = useMemo(() => {
        return form?.data?.some((field) => field?.key === 'PageBreak');
    }, [form]);

    const { pages, fieldPageMap, pageNames } = useMemo(() => {
        if (!hasPageBreak) return { pages: [], fieldPageMap: {}, pageNames: [] };

        const pages = [];
        const pageNames = [];
        let currentPageElements = [];
        const fieldPageMap = {};
        let currentPageIndex = 0;

        form.data.forEach((form, index) => {
            if (index === 0 && form.key !== 'PageBreak') {
                pageNames.push('');
            }
            if (form.key === 'PageBreak') {
                if (currentPageElements.length > 0) {
                    pages.push(currentPageElements);
                    currentPageElements = [];
                    currentPageIndex++;
                }
                pageNames.push(`${form?.fieldName || ''}`);
            } else {
                const fieldName = `${form.name || form.key}-${form._id}`;
                fieldPageMap[fieldName] = currentPageIndex;
                currentPageElements.push(form);
            }
        });

        if (currentPageElements.length > 0) {
            pages.push(currentPageElements);
        }

        return { pages, fieldPageMap, pageNames };
    }, [form, hasPageBreak]);

    const handleUserSelect = (userId) => {
        const tempSelectedUserOption = [...selectedUserOption];
        const indexOfUserId = tempSelectedUserOption.indexOf(userId);

        if (indexOfUserId !== -1) {
            tempSelectedUserOption.splice(indexOfUserId, 1);
        } else {
            tempSelectedUserOption.push(userId);
        }

        setSelectedUserOption(tempSelectedUserOption);
    };
    const onSubmit = async (data, submitMode = true) => {
        try {
            setIsSubmitting(true);
            setIsDraftFormSubmitting(true);
            const chatroomId = userFormData?.chatroom || roomId;
            const userFormDataId = userFormData?._id || userFormData?.id;
            if (sharedFormId) {
                const res = await handleFormSubmission({
                    data,
                    form,
                    formType: form?.type,
                    roomId: null,
                    position,
                    user,
                    team: null,
                    currRoom: null,
                    submitMode: null,
                    setIsSubmitting,
                    sharedFormSubmission: true,
                    sharedFormId,
                    visibleTo: form?.visibleTo || ['user', 'supervisor', 'doctor', 'family'],
                });
                if (res) {
                    setSharedFormResponse(true);
                    toast.success('Form successfully submitted');
                }
                return;
            }
            if (sharedUserFormDataId) {
                const res = await handleFormSubmission({
                    data,
                    form,
                    formType: form?.type,
                    roomId: null,
                    position,
                    user,
                    team: null,
                    currRoom: null,
                    submitMode,
                    setIsSubmitting,
                    sharedUserFormDataSubmission: true,
                    sharedUserFormDataId,
                    visibleTo: form?.visibleTo || ['user', 'supervisor', 'doctor', 'family'],
                });
                if (res) {
                    setSharedUserFormDataResponse(true);
                    toast.success('Form successfully submitted');
                }
                return;
            }
            const response = await handleFormSubmission({
                data,
                form,
                userFormDataId,
                formType: form?.type,
                roomId: chatroomId,
                position,
                user,
                team,
                currRoom,
                submitMode,
                selectedUserOption,
                taskId,
                subTasks,
                updateTaskFormStatus,
                getAllTasks,
                setIsSubmitting,
                setUserData,
                visibleTo: form?.visibleTo || ['user', 'supervisor', 'doctor', 'family'],
            });

            if (response) {
                toast.success('Form successfully submitted');

                setUserData(
                    form.data.map((form) => ({
                        ...form,
                        value: response?.data?.find((f) => form._id === f.fieldName.split('-')[1])?.value,
                    }))
                );
                if (response?.submissionType === 'completed' && taskId && subTasks?.length) {
                    updateTaskFormStatus({ formId: response?.form, userId: response?.user, taskId, subTasks });
                }
                if (getAllTasks) window.location.reload();
                if (handleClose) handleClose();
            }
        } catch (e) {
            console.log(e, 'FORM SUBMITTED ERROR');
            toast.error(e.message);
        } finally {
            setIsSubmitting(false);
            setIsDraftFormSubmitting(false);
        }
    };

    const handleSaveDraft = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to mark this as a draft?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, save as draft',
            cancelButtonText: 'No, cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                setIsDraft(true);
                handleSubmit((data) => onSubmit(data, false))();
            }
        });
    };

    const handleFormSubmit = async (submitMode = true, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        try {
            if (hasPageBreak) {
                const formData = getValues();
                console.debug('[Form] Submission data:', formData);

                const isValid = await trigger();
                if (!isValid) {
                    console.warn('[Form] Validation errors:', errors);
                    const firstError = Object.keys(errors)[0];
                    const pageIndex = fieldPageMap[firstError];
                    if (pageIndex !== undefined) {
                        setCurrentPage(pageIndex);
                        setTimeout(() => {
                            const fieldSelector = `[name="${firstError}"]`;
                            const field = formRef.current?.querySelector(fieldSelector);
                            if (field) {
                                const pageContainer = field.closest('.form-page');
                                pageContainer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                setTimeout(() => {
                                    field.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'center',
                                        inline: 'nearest',
                                    });
                                    field.focus({ preventScroll: true });

                                    field.style.boxShadow = '0 0 8px 2px rgba(255, 0, 0, 0.7)';
                                    field.style.transition = 'box-shadow 0.3s ease-in-out';

                                    setTimeout(() => {
                                        if (field) {
                                            field.style.boxShadow = 'none';
                                        }
                                    }, 3000);
                                }, 200);
                            }
                        }, 100);
                    }

                    return;
                }

                await onSubmit(formData, submitMode);
            } else {
                await handleSubmit(async (data) => {
                    console.debug('[Form] Submission data:', data);
                    await onSubmit(data, submitMode);
                })(e);
            }
        } catch (error) {
            console.error('[Form] Submission error:', error);
            toast.error('Submission failed. Please try again.');
        }
    };

    const updateSeenFields = (page) => {
        try {
            const pageFields = (pages[page] || []).map((field) => field?._id);
            addSeenFields({ seenFields: pageFields });
        } catch (e) {
            console.log(e);
        }
    };
    useEffect(() => {
        if (hasPageBreak && pages && pages.length > 0) {
            setTimeout(() => {
                updateSeenFields(0);
            }, [1000]);
        }
    }, [pages]);

    useEffect(() => {
        clearSeenFields();
    }, []);

    const renderFormElements = () => {
        if (hasPageBreak) {
            return (
                <div css={staticPreviewContainerStyles}>
                    {pages.length > 1 && (
                        <FormStepper
                            pages={pages}
                            pageNames={pageNames}
                            currentPage={currentPage}
                            onPageChange={(page) => {
                                setCurrentPage(page);
                                setValidationError(null);
                                updateSeenFields(page);
                            }}
                            fieldPageMap={fieldPageMap}
                            errors={errors}
                        />
                    )}
                    <div css={getFormContainerStyles(pages.length > 1)}>
                        {pages.flatMap((pageElements, pageIndex) =>
                            pageElements.map((form) => (
                                <div
                                    key={form._id}
                                    className={`form-element ${pageIndex === currentPage ? 'visible' : 'hidden'}`}
                                    css={css`
                                        overflow-wrap: break-word;
                                        display: ${pageIndex === currentPage ? 'block' : 'none'};
                                    `}
                                >
                                    <StaticPreview
                                        formElements={[enhanceElement(form)]}
                                        elementRef={register}
                                        errors={errors}
                                        isDraft={isDraft}
                                        createForm={true}
                                        userFormData={userFormData}
                                        isPreview={isPreview}
                                        setError={setError}
                                        clearErrors={clearErrors}
                                        setValue={setValue}
                                    />
                                </div>
                            ))
                        )}

                        {pages.length > 1 && (
                            <FormPagination
                                pages={pages}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                setValidationError={setValidationError}
                                updateSeenFields={updateSeenFields}
                            />
                        )}

                        {validationError && (
                            <div
                                css={css`
                                    color: #dc3545;
                                    margin: 1rem;
                                    text-align: center;
                                `}
                            >
                                {validationError}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <StaticPreview
                formElements={form.data?.map((element) => {
                    if (element.data?.length) {
                        element.data = element.data.map((subElement) => ({
                            ...subElement,
                            defaultValue: subElement?.autofill ? user[subElement?.autofill] : undefined,
                        }));
                    }
                    element.defaultValue = element.autofill ? user[element.autofill] : undefined;
                    return enhanceElement(element);
                })}
                elementRef={register}
                errors={errors}
                isDraft={isDraft}
                createForm={true}
                userFormData={userFormData}
                isPreview={isPreview}
                setError={setError}
                clearErrors={clearErrors}
                setValue={setValue}
            />
        );
    };

    return (
        <Fragment>
            <Form ref={formRef} onSubmit={(e) => handleFormSubmit(true, e)} noValidate>
                <div
                    className="p-2"
                    css={css`
                        min-height: 150px;
                        height: fit-content;
                    `}
                >
                    {renderFormElements()}

                    {formType === 'multipleSubmission' && (!submissionType || submissionType === 'draft') && (
                        <div
                            css={css`
                                padding: 1.5rem 1rem 1.5rem 1.5rem;
                            `}
                        >
                            Form participants
                            <ParticipantsContainer className="border p-2 rounded">
                                {channelMembers?.length > 0 ? (
                                    channelMembers.map((member) => {
                                        const id = member.id || member._id;
                                        return (
                                            <UserInRoom
                                                key={id}
                                                user={member}
                                                onSelect={handleUserSelect}
                                                isSelected={selectedUserOption?.includes(id)}
                                            />
                                        );
                                    })
                                ) : (
                                    <div>
                                        <h5>No channel members</h5>
                                        <p>Invite participants to the channel.</p>
                                    </div>
                                )}
                            </ParticipantsContainer>
                        </div>
                    )}
                </div>
                {isPreview ? null : (
                    <div className="text-center">
                        <Button
                            type="submit"
                            css={css`
                                font-size: 16px;
                                border-radius: 16px;
                                margin-top: 1rem;
                                padding: 0.5rem 2rem;
                                width: 25%;
                            `}
                            disabled={userData.length || isSubmitting || isDraftFormSubmitting || isDisabled}
                        >
                            {isSubmitting || isDraftFormSubmitting ? <VscLoading className="spin" /> : 'Submit'}
                        </Button>

                        {form && isAllowedDraftFormType(form) && !sharedFormId && (
                            <Button
                                type="button"
                                variant="secondary"
                                css={css`
                                    font-size: 16px;
                                    border-radius: 16px;
                                    margin-top: 1rem;
                                    margin-left: 1rem;
                                    padding: 0.5rem 2rem;
                                    width: 25%;
                                `}
                                disabled={userData.length || isSubmitting || isDraftFormSubmitting || isDisabled}
                                onClick={handleSaveDraft}
                            >
                                {isSubmitting || isDraftFormSubmitting ? (
                                    <VscLoading className="spin" />
                                ) : (
                                    'Save as Draft'
                                )}
                            </Button>
                        )}
                    </div>
                )}
            </Form>
            {user && user?._id && !hideUserFormDataDiscussion && <UserFormDataDiscussion userFormData={userFormData} />}
        </Fragment>
    );
};

const mapStateToProps = (state) => ({
    position: state.user.position,
    user: state.user.user,
});

export default connect(mapStateToProps, { updateTaskFormStatus, addSeenFields, clearSeenFields })(FormSubmissionModal);
